import React from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Button, Typography, useMediaQuery } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material'

import LoginBannerGift from './loginBannerGift'

interface SignInPromoBannerProps {
    title: string
    subtitle: string
    codeLabel: string
    promoCode: string
    ctaLabel: string
    isRtl: boolean
    onLoginClick: () => void
}

const titleSx = {
    fontWeight: 700,
    fontSize: { xs: '14px', md: '16px' },
    color: 'text.primary',
    lineHeight: 1.3,
    mb: 0.25,
    wordBreak: 'break-word'
} as const

const subtitleSx = {
    color: 'text.secondary',
    fontWeight: 500,
    fontSize: { xs: '11px', md: '12px' },
    lineHeight: 1.4
} as const

/** Decorative cloud */
const Cloud: React.FC<{ sx?: SxProps<Theme> }> = ({ sx }) => (
    <Box
        component="svg"
        viewBox="0 0 70 26"
        aria-hidden
        sx={{
            position: 'absolute',
            color: 'common.white',
            fill: 'currentColor',
            opacity: 0.85,
            pointerEvents: 'none',
            ...sx
        }}
    >
        {/* Overlapping circles + rounded base = smooth puffy cloud */}
        <circle cx="13" cy="17" r="9" />
        <circle cx="29" cy="12" r="12" />
        <circle cx="46" cy="14" r="10" />
        <circle cx="58" cy="18" r="8" />
        <rect x="13" y="14" width="45" height="12" rx="6" />
    </Box>
)

const CodePill: React.FC<{
    codeLabel: string
    promoCode: string
}> = ({ codeLabel, promoCode }) => (
    <Box
        component="span"
        sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: { xs: '10px', md: '12px' },
            py: { xs: '3px', md: '4px' },
            maxWidth: '100%',
            bgcolor: theme.palette.signInPromoBanner.pillBg,
            border: '1px solid',
            borderColor: theme.palette.signInPromoBanner.pillBorder,
            borderRadius: '10px'
        })}
    >
        <Typography
            component="span"
            variant="body2"
            sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: { xs: '11px', md: '12px' }
            }}
        >
            {codeLabel}
        </Typography>
        <Typography
            component="span"
            variant="body2"
            sx={{
                fontWeight: 500,
                fontSize: { xs: '11px', md: '12px' },
                letterSpacing: '0.5px',
                color: 'text.primary'
            }}
        >
            {promoCode}
        </Typography>
    </Box>
)

const SignInPromoBanner: React.FC<SignInPromoBannerProps> = ({
    title,
    subtitle,
    codeLabel,
    promoCode,
    ctaLabel,
    isRtl,
    onLoginClick
}) => {
    const rowDirection = isRtl ? 'row-reverse' : 'row'
    const textAlign = isRtl ? 'right' : 'left'
    const muiTheme = useTheme()
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))

    return (
        <Box
            onClick={isMobile ? onLoginClick : undefined}
            role={isMobile ? 'button' : undefined}
            aria-label={isMobile ? ctaLabel : undefined}
            sx={(theme) => ({
                cursor: { xs: 'pointer', md: 'default' },
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: { xs: '12px 10px', md: '0 32px' },
                height: { xs: 'auto', md: 100 },
                minHeight: { xs: 80 },
                borderRadius: { xs: '14px', md: '20px' },
                background: theme.palette.signInPromoBanner.background,
                overflow: 'hidden',
                position: 'relative'
            })}
        >
            {/* Decorative clouds */}
            <Cloud sx={{ top: { xs: 5, md: 8 }, left: '44%', width: { xs: 34, md: 48 } }} />
            <Cloud sx={{ top: { xs: 9, md: 14 }, right: '13%', width: { xs: 26, md: 36 }, opacity: 0.7 }} />
            <Cloud sx={{ top: { xs: -6, md: -8 }, right: { xs: 20, md: 36 }, width: { xs: 40, md: 56 } }} />

            {/* gift | text+pill | outlined button */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: rowDirection,
                    alignItems: 'center',
                    gap: { xs: 1.5, md: 3 },
                    width: '100%',
                    minWidth: 0,
                    position: 'relative'
                }}
            >
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                    <LoginBannerGift width={90} height={76} />
                </Box>
                <Box sx={{ flexShrink: 0, display: { xs: 'block', sm: 'none' } }}>
                    <LoginBannerGift width={62} height={53} />
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: rowDirection,
                        alignItems: 'center',
                        gap: { xs: 1.5, md: 2 },
                        textAlign
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            // Keep text/pill clear of the absolute arrow CTA on mobile
                            ...(isRtl ? { pl: { xs: '28px', md: 0 } } : { pr: { xs: '28px', md: 0 } })
                        }}
                    >
                        <Typography variant="body1" sx={titleSx}>
                            {title}
                        </Typography>
                        <Typography variant="body2" sx={subtitleSx}>
                            {subtitle}
                        </Typography>
                        <Box sx={{ mt: 0.75 }}>
                            <CodePill codeLabel={codeLabel} promoCode={promoCode} />
                        </Box>
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={onLoginClick}
                        sx={(theme) => ({
                            display: { xs: 'none', md: 'inline-flex' },
                            flexShrink: 0,
                            alignSelf: 'center',
                            px: { xs: '16px', md: '24px' },
                            py: { xs: '7px', md: '10px' },
                            fontSize: { xs: '12px', md: '14px' },
                            fontWeight: 600,
                            lineHeight: 1.3,
                            minWidth: { xs: 0, md: 130 },
                            borderRadius: { xs: '10px', md: '12px' },
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            color: theme.palette.signInPromoBanner.ctaBg,
                            borderColor: theme.palette.signInPromoBanner.ctaBg,
                            bgcolor: 'transparent',
                            '&:hover': {
                                borderColor: theme.palette.signInPromoBanner.ctaHoverBg,
                                color: theme.palette.signInPromoBanner.ctaHoverBg,
                                bgcolor: alpha(theme.palette.signInPromoBanner.ctaBg, 0.08)
                            }
                        })}
                    >
                        {ctaLabel}
                    </Button>
                </Box>
            </Box>

            {/* Mobile: decorative filled arrow, pinned to the bottom corner (whole banner is tappable) */}
            <Box
                aria-hidden
                sx={(theme) => ({
                    display: { xs: 'inline-flex', md: 'none' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'absolute',
                    bottom: 12,
                    ...(isRtl ? { left: 16 } : { right: 16 }),
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: theme.palette.signInPromoBanner.ctaBg,
                    color: 'common.white',
                    pointerEvents: 'none'
                })}
            >
                {isRtl ? (
                    <ChevronLeftIcon sx={{ fontSize: 14 }} />
                ) : (
                    <ChevronRightIcon sx={{ fontSize: 14 }} />
                )}
            </Box>
        </Box>
    )
}

export { SignInPromoBanner }
export type { SignInPromoBannerProps }
